import { useLingui } from '@lingui/react/macro';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import type { z } from 'zod';

import { useIsMobile } from '@/shared/mobile';
import { usePermissions } from '@/shared/permissions';
import { reportError } from '@/shared/telemetry';
import { Button } from '@/shared/ui/button';

import { defaultSkeleton, type Block, type BlockPresentation, type BlockRun, type SourceMap } from './block';
import { BlockFrame } from './block-frame';
import { useBlockHost } from './host';
import { BlockSkeleton, crashCode, EmptyState, ErrorState } from './states';
import { isAllowed, useBlockData } from './use-block-data';

export interface BlockRendererProps<PS extends z.ZodType, S extends SourceMap> {
  readonly block: Block<PS, S>;
  /** A única entrada: na tela vem da URL (pelo hospedeiro), na página vem do documento. */
  readonly params: z.input<PS>;
  /** Força a apresentação (prévia, catálogo); o padrão segue o corte de 768 px (U12). */
  readonly presentation?: BlockPresentation;
  /** Vazio do hospedeiro ("Limpar filtros"), que vence o `empty` do bloco. */
  readonly empty?: ReactNode;
  readonly actions?: ReactNode;
  readonly headingLevel?: 2 | 3;
  /** Nome acessível da região, quando o mesmo bloco aparece mais de uma vez na tela. */
  readonly label?: string;
  readonly className?: string;
  /** `false` para fonte local (navegador): sem "Atualizado há" nem o botão Atualizar no cabeçalho. */
  readonly refreshable?: boolean;
}

/** Desenha o bloco dentro do `BlockFrame`: dados, estados, apresentação web ou iPhone e intenções. */
export function BlockRenderer<PS extends z.ZodType, S extends SourceMap>(props: BlockRendererProps<PS, S>) {
  const { i18n } = useLingui();
  return (
    <BlockErrorBoundary blockId={props.block.id} title={i18n._(props.block.title)}>
      <BlockContent {...props} />
    </BlockErrorBoundary>
  );
}

function BlockContent<PS extends z.ZodType, S extends SourceMap>({
  block,
  params,
  presentation: forced,
  empty,
  actions,
  headingLevel,
  label,
  className,
  refreshable = true,
}: BlockRendererProps<PS, S>) {
  const { i18n } = useLingui();
  const host = useBlockHost();
  const isMobile = useIsMobile();
  const permissions = usePermissions();
  const result = useBlockData(block, params);
  const presentation: BlockPresentation = forced ?? (isMobile ? 'mobile' : 'web');

  const declared = new Map((block.actions ?? []).map((action) => [action.id, action]));
  const canRun = (actionId: string) => {
    const action = declared.get(actionId);
    if (!action) return false;
    return !action.permission || isAllowed(host.surface, permissions.model, action.permission);
  };
  const run: BlockRun = (actionId, input) => {
    const action = declared.get(actionId);
    if (!action) {
      if (import.meta.env.DEV) throw new Error(`bloco ${block.id} pediu ação não declarada: ${actionId}`);
      return;
    }
    const parsed = action.input ? action.input.safeParse(input ?? {}) : { success: true as const, data: input };
    if (!parsed.success) {
      if (import.meta.env.DEV) throw new Error(`bloco ${block.id}: input inválido para ${actionId}`);
      return;
    }
    void host.run(actionId, parsed.data);
  };

  const View = block.render[presentation];
  const firstShape = Object.values(block.sources)[0]?.shape;
  const skeleton = <BlockSkeleton {...(block.skeleton?.[presentation] ?? defaultSkeleton(firstShape, presentation))} />;
  const emptyAction = block.empty?.action;
  const emptyNode =
    empty ??
    (block.empty ? (
      <EmptyState
        title={i18n._(block.empty.title)}
        description={block.empty.description ? i18n._(block.empty.description) : undefined}
        {...(block.empty.icon ? { icon: block.empty.icon } : {})}
        compact={presentation === 'mobile'}
        action={
          emptyAction && canRun(emptyAction.id) ? (
            <Button variant="secondary" size={presentation === 'mobile' ? 'lg' : 'default'} onClick={() => run(emptyAction.id, emptyAction.input)}>
              {i18n._(emptyAction.label)}
            </Button>
          ) : undefined
        }
      />
    ) : undefined);

  return (
    <BlockFrame
      title={i18n._(block.title)}
      state={result.state}
      presentation={presentation}
      updatedAt={refreshable ? result.updatedAt : undefined}
      onRefresh={refreshable ? () => void result.refetch() : undefined}
      refreshing={result.isFetching && result.state !== 'loading'}
      dimmed={result.isPlaceholderData}
      skeleton={skeleton}
      empty={emptyNode}
      error={result.error}
      missing={result.missing}
      actions={actions}
      label={label}
      {...(headingLevel ? { headingLevel } : {})}
      {...(className ? { className } : {})}
    >
      {result.data ? <View params={result.params} data={result.data} run={run} canRun={canRun} surface={host.surface} /> : null}
    </BlockFrame>
  );
}

interface BoundaryState {
  readonly error: unknown;
  readonly failed: boolean;
}

/** O que vai ao console e à telemetria de um bloco que quebrou: nunca o dado do registro. */
export function blockCrashReport(blockId: string, error: unknown) {
  return error instanceof Error
    ? { block: blockId, name: error.name, message: error.message, stack: error.stack }
    : { block: blockId, name: typeof error, message: undefined, stack: undefined };
}

/**
 * O erro de um bloco não derruba a tela nem a página (08 §4.4). Vai ao console também em produção
 * (sem dado do registro) e o cartão mostra o código curto (`render · TypeError`) para o suporte.
 */
export class BlockErrorBoundary extends Component<{ readonly blockId: string; readonly title: string; readonly children: ReactNode }, BoundaryState> {
  override state: BoundaryState = { error: undefined, failed: false };

  static getDerivedStateFromError(error: unknown): BoundaryState {
    return { error, failed: true };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo) {
    const report = blockCrashReport(this.props.blockId, error);
    console.error(`bloco ${report.block} falhou: ${report.name}${report.message ? `: ${report.message}` : ''}`, report.stack ?? '', import.meta.env.DEV ? info.componentStack : '');
    reportError(error, { 'error.source': 'block.boundary', 'uyzy.block': this.props.blockId });
  }

  override render() {
    if (!this.state.failed) return this.props.children;
    return (
      <section data-slot="block-frame" data-state="crashed" className="rounded-card border border-border bg-card">
        <ErrorState title={this.props.title} code={crashCode('render', this.state.error)} onRetry={() => this.setState({ error: undefined, failed: false })} />
      </section>
    );
  }
}
